import type {
  AssetComponentContextResponseDto,
  AssetComponentResponseDto,
  ListAssetComponentContextsResponseDto,
  ListAssetComponentsResponseDto,
  UpdateAssetComponentRequestDto,
} from '@evimo/types';
import { ResponseError } from '@evimo/client/runtime';
import {
  assetComponentRequestInit,
  assetComponentsApi,
} from '~/app/services/asset-component/asset-component-api-instances';

export type ListAssetComponentsParams = {
  page: number;
  limit: number;
  q?: string;
};

async function readClientError(error: unknown): Promise<never> {
  if (error instanceof ResponseError) {
    const message = await error.response.text();
    throw new Error(message || `Request failed (${error.response.status})`);
  }
  throw error;
}

export const AssetComponentService = {
  async list(
    organizationId: string,
    assetId: string,
    params: ListAssetComponentsParams,
  ): Promise<ListAssetComponentsResponseDto> {
    try {
      const result = await assetComponentsApi.assetComponentControllerList(
        {
          organizationId,
          assetId,
          page: params.page,
          limit: params.limit,
          ...(params.q?.trim() ? { q: params.q.trim() } : {}),
        },
        assetComponentRequestInit,
      );
      return result as unknown as ListAssetComponentsResponseDto;
    } catch (error) {
      return readClientError(error);
    }
  },

  async listContexts(
    organizationId: string,
    assetId: string,
  ): Promise<ListAssetComponentContextsResponseDto> {
    try {
      const result =
        await assetComponentsApi.assetComponentControllerListContexts(
          { organizationId, assetId },
          assetComponentRequestInit,
        );
      return result as unknown as ListAssetComponentContextsResponseDto;
    } catch (error) {
      return readClientError(error);
    }
  },

  async update(
    organizationId: string,
    assetId: string,
    componentId: string,
    dto: UpdateAssetComponentRequestDto,
  ): Promise<AssetComponentResponseDto> {
    try {
      const result = await assetComponentsApi.assetComponentControllerUpdate(
        {
          organizationId,
          assetId,
          componentId,
          updateAssetComponentRequestDto: dto as never,
        },
        assetComponentRequestInit,
      );
      return result as unknown as AssetComponentResponseDto;
    } catch (error) {
      return readClientError(error);
    }
  },

  delete(
    organizationId: string,
    assetId: string,
    componentId: string,
  ): Promise<void> {
    return assetComponentsApi
      .assetComponentControllerDelete(
        { organizationId, assetId, componentId },
        assetComponentRequestInit,
      )
      .catch(readClientError);
  },
};

export type { AssetComponentContextResponseDto };
